import React from 'react';
import { DownloadIcon, FolderIcon, FolderInputIcon, PencilIcon, StarIcon, Trash2Icon, XIcon } from 'lucide-react';
import { FILE_TYPE_META } from '../../utils/fileTypes';
import { folderStats, isFile, pathTo } from '../../utils/fileTree';
import { formatDateTime, formatSize } from '../../utils/format';
import type { FsNode } from '../../types/files';
interface DetailsPanelProps {
  node: FsNode;
  nodes: FsNode[];
  onClose: () => void;
  onDownload: (node: FsNode) => void;
  onToggleStar: (node: FsNode) => void;
  onRename: (node: FsNode) => void;
  onMove: (node: FsNode) => void;
  onTrash: (node: FsNode) => void;
  onGoToFolder: (id: string | null) => void;
}
export function DetailsPanel({
  node,
  nodes,
  onClose,
  onDownload,
  onToggleStar,
  onRename,
  onMove,
  onTrash,
  onGoToFolder
}: DetailsPanelProps) {
  const location = pathTo(nodes, node.id);
  const meta = isFile(node) ? FILE_TYPE_META[node.type] : null;
  const Icon = meta?.icon ?? FolderIcon;
  const stats = node.kind === 'folder' ? folderStats(nodes, node.id) : null;
  return <aside aria-label={`Details for ${node.name}`} className="flex w-full shrink-0 flex-col border-t border-slate-200 bg-white lg:h-full lg:w-80 lg:border-l lg:border-t-0" data-id="element-18" data-mp="1rpz7bubmzlay">
      <div className="flex items-start justify-between gap-2 border-b border-slate-200 px-4 py-3" data-id="element-19" data-mp="20qnj0idk59ko">
        <h2 className="text-sm font-semibold text-slate-900" data-id="element-20" data-mp="25q9vuqph4nsy">Details</h2>
        <button type="button" onClick={onClose} aria-label="Close details" className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-21" data-mp="25nw1yl6j5hmz">
          <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-22" data-mp="2kgz8zymhwczg" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto" data-id="element-23" data-mp="2oib3ahgbdmdz">
        <div className="flex h-40 items-center justify-center border-b border-slate-200 bg-slate-50" data-id="element-24" data-mp="2tfb70lpoyd1x">
          {isFile(node) && node.thumbnailUrl ? <img src={node.thumbnailUrl} alt={node.name} className="h-full w-full object-cover" data-id="element-25" data-mp="tdl41jqvbei8" /> : <Icon className={['h-14 w-14', node.kind === 'folder' ? 'text-[#0176d3]' : meta?.iconClass ?? ''].join(' ')} aria-hidden="true" data-id="element-26" data-mp="1346cdywcfxec" />}
        </div>

        <div className="px-4 py-3" data-id="element-27" data-mp="1827wmn5ia2re">
          <p className="break-words text-sm font-semibold text-slate-900" data-id="element-28" data-mp="18167jernnfqo">
            {node.name}
          </p>
          <p className="mt-0.5 text-xs text-slate-500" data-id="element-29" data-mp="1m0pguaw7cx4k">
            {node.kind === 'folder' ? `Folder · ${stats?.fileCount ?? 0} files` : `${meta?.label} · ${formatSize(node.sizeKb)} · v${node.version}`}
          </p>

          <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-30" data-mp="3iuag734lrkdy">
            <ActionButton label={node.kind === 'folder' ? 'Download zip' : 'Download'} icon={DownloadIcon} onClick={() => onDownload(node)} primary data-id="element-31" data-mp="3ivtwgtq9dvfq" />
            <ActionButton label={node.starred ? 'Unstar' : 'Star'} icon={StarIcon} onClick={() => onToggleStar(node)} data-id="element-32" data-mp="23ajo1pck5wbh" />
            <ActionButton label="Rename" icon={PencilIcon} onClick={() => onRename(node)} data-id="element-33" data-mp="1tk715qh9tr26" />
            <ActionButton label="Move" icon={FolderInputIcon} onClick={() => onMove(node)} data-id="element-34" data-mp="1kktqwvlboyge" />
            <ActionButton label="Delete" icon={Trash2Icon} onClick={() => onTrash(node)} danger data-id="element-35" data-mp="16m3rh9b16mov" />
          </div>
        </div>

        {node.description && <p className="border-t border-slate-100 px-4 py-3 text-sm text-slate-600" data-id="element-36" data-mp="30zv3gfgu1inh">
            {node.description}
          </p>}

        <dl className="space-y-2.5 border-t border-slate-100 px-4 py-3 text-sm" data-id="element-37" data-mp="2g9r6m1s7jzkn">
          <Row label="Owner" value={node.owner} data-id="element-38" data-mp="2baxzqs7ff6tg" />
          <Row label="Last modified" value={formatDateTime(node.modifiedAt)} data-id="element-39" data-mp="2bc94au8p4ilm" />
          <Row label="Modified by" value={node.modifiedBy} data-id="element-40" data-mp="26b1wwwagtlfh" />
          {isFile(node) && <Row label="Version" value={`v${node.version}`} data-id="element-41" data-mp="21d0co81azg2f" />}
          {node.kind === 'folder' && stats && <Row label="Contents" value={formatSize(stats.sizeKb)} data-id="element-42" data-mp="1xbolvz6p0osn" />}
          <div data-id="element-43" data-mp="1skgch35d53fh">
            <dt className="text-xs font-medium text-slate-500" data-id="element-44" data-mp="1sjwslgw5k4yt">Location</dt>
            <dd className="mt-0.5" data-id="element-45" data-mp="1nld3x8q6cf2r">
              <button type="button" onClick={() => onGoToFolder(location.length ? location[location.length - 1].id : null)} className="rounded text-left text-sm font-medium text-[#0176d3] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3]" data-id="element-46" data-mp="1nkk2mn9zihs0">
                {location.length > 0 ? ['Loan Documents', ...location.map(item => item.name)].join(' / ') : 'Loan Documents'}
              </button>
            </dd>
          </div>
          <Row label="Sharing" value="Loan team · can edit" data-id="element-47" data-mp="3uaf2bw9h54hi" />
        </dl>

        <div className="border-t border-slate-100 px-4 py-3" data-id="element-48" data-mp="31zw7eutwyob">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-49" data-mp="31l8839h1z122">
            Activity
          </h3>
          <ol className="mt-2 space-y-2.5 text-sm" data-id="element-50" data-mp="36l2xt9p3dklj">
            {[{
            who: node.modifiedBy,
            what: 'updated this item',
            when: formatDateTime(node.modifiedAt)
          }, {
            who: 'Priya Raman',
            what: 'shared with the loan team',
            when: 'Aug 20, 2026, 9:12 AM'
          }, {
            who: node.owner,
            what: 'created this item',
            when: 'Aug 17, 2026, 2:04 PM'
          }].map((entry, index) => <li key={index} data-id="element-51" data-mp="2bq7h8nyfjlob" data-mpi={index}>
                <p className="text-slate-800" data-id="element-52" data-mp="2br0gl3v278wf">
                  <span className="font-medium" data-id="element-53" data-mp="2pj6vkmmh79ua">{entry.who}</span> {entry.what}
                </p>
                <p className="text-xs text-slate-500" data-id="element-54" data-mp="2kj2dis5x671y">{entry.when}</p>
              </li>)}
          </ol>
        </div>
      </div>
    </aside>;
}
function Row({
  label,
  value
}: {
  label: string;
  value: string;
}) {
  return <div data-id="element-55" data-mp="1stz8392aqqnq">
      <dt className="text-xs font-medium text-slate-500" data-id="element-56" data-mp="1su7mj5e2i411">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-800" data-id="element-57" data-mp="26t79m8lxmav3">{value}</dd>
    </div>;
}
interface ActionButtonProps {
  label: string;
  icon: typeof DownloadIcon;
  onClick: () => void;
  primary?: boolean;
  danger?: boolean;
}
function ActionButton({
  label,
  icon: Icon,
  onClick,
  primary,
  danger
}: ActionButtonProps) {
  const tone = primary ? 'bg-[#0176d3] text-white hover:bg-[#014486]' : danger ? 'border border-red-300 bg-white text-red-700 hover:bg-red-50' : 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50';
  return <button type="button" onClick={onClick} className={`inline-flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0176d3] focus-visible:ring-offset-1 ${tone}`} data-id="element-58" data-mp="q0259xjyukv3">
      <Icon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-59" data-mp="1iqtwegm6cjbk" />
      {label}
    </button>;
}